/**
 * The apps omnium can open, with register and remove.
 *
 * An app is only opened for a workflow it is ticked on, so the list says which
 * workflows open in each one, and "Choose apps" on a workflow card changes it.
 */

import { useState } from "react";
import { ExternalLink, Plus, Trash2, TriangleAlert } from "lucide-react";

import {
  Button,
  Card,
  Empty,
  Field,
  Input,
  Pill,
  Problem,
  Section,
  Segmented,
  Select,
  Stack,
  Table,
  Td,
  Th,
} from "@/components/kit";
import { listDefinitions } from "@/lib/api/client";
import {
  appKindLabel,
  codeFromName,
  newAppProblem,
  registerApp,
  removeApp,
  roleDoing,
  type AppRow,
  type NewApp,
} from "@/lib/api/workflows";
import { displayName } from "@/lib/format";
import { errorText, useApi } from "@/spa/useApi";

import { Dialog } from "./Dialog";
import styles from "./workflows.module.css";

export function AppsSection({
  apps,
  error,
  onChanged,
}: {
  apps: AppRow[] | undefined;
  error?: string;
  onChanged: () => void;
}) {
  const [registering, setRegistering] = useState(false);
  const [removing, setRemoving] = useState<AppRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  return (
    <Section
      title="Apps"
      note="An app is a web page omnium opens in a frame. People do their work in it. A workflow only opens in the apps chosen for it."
      actions={
        <Button tone="primary" onClick={() => setRegistering(true)}>
          <Plus size={15} aria-hidden />
          Register an app
        </Button>
      }
    >
      <Stack>
        {notice && (
          <p role="status" className={styles.notice}>
            {notice}
          </p>
        )}
        {error ? (
          <Problem>Could not load the apps: {error}</Problem>
        ) : !apps ? (
          <Card>
            <Empty title="Loading…" />
          </Card>
        ) : apps.length === 0 ? (
          <Card>
            <Empty
              title="No apps registered yet"
              note="Register a scoring desk or an operations console, then choose it on a workflow."
              action={<Button onClick={() => setRegistering(true)}>Register an app</Button>}
            />
          </Card>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>App</Th>
                <Th>Address</Th>
                <Th>Workflows that open in it</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {apps.map((app) => (
                <tr key={app.id} id={`app-${app.code}`}>
                  <Td strong>
                    <div className={styles.appName}>
                      {app.name}
                      <Pill>{appKindLabel(app.kind)}</Pill>
                      {app.sportCode && <Pill>{displayName(app.sportCode)} only</Pill>}
                    </div>
                    <p className={styles.appDesc}>
                      {app.description ?? "No description."} Code: {app.code}.
                    </p>
                  </Td>
                  <Td>
                    <a className={styles.url} href={app.url} target="_blank" rel="noreferrer">
                      {app.url}
                      <ExternalLink size={12} aria-hidden />
                    </a>
                  </Td>
                  <Td>
                    {app.workflows.length === 0 ? (
                      <span className={styles.warnText}>
                        <TriangleAlert size={14} aria-hidden />
                        None yet
                      </span>
                    ) : (
                      <div className={styles.opensList}>
                        {app.workflows.map((workflow) => (
                          <span key={`${workflow.code}/${workflow.role}`}>
                            <a href={`#workflow-${workflow.code}`}>{workflow.name}</a>{" "}
                            <span className={styles.small}>
                              · {roleDoing(workflow.role)}
                              {workflow.isDefault ? " · default" : ""}
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <Button tone="subtle" onClick={() => setRemoving(app)}>
                      <Trash2 size={14} aria-hidden />
                      Remove
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Stack>

      {registering && (
        <RegisterAppDialog
          onClose={() => setRegistering(false)}
          onDone={(row) => {
            setRegistering(false);
            setNotice(
              `Registered ${row.name}. It opens nowhere yet: press Choose apps on a workflow and tick it.`,
            );
            onChanged();
          }}
        />
      )}
      {removing && (
        <RemoveAppDialog
          app={removing}
          onClose={() => setRemoving(null)}
          onDone={() => {
            setRemoving(null);
            setNotice(`Removed ${removing.name}.`);
            onChanged();
          }}
        />
      )}
    </Section>
  );
}

const EMPTY_APP: NewApp = { code: "", name: "", url: "", kind: "scoring", sportCode: "", description: "" };

const KIND_HINT: Record<NewApp["kind"], string> = {
  scoring: "A scoring desk. It opens on one fixture and records play as it happens.",
  operations:
    "An operations app. It edits records, such as an event's medal table or a fixture's result.",
};

function RegisterAppDialog({ onClose, onDone }: { onClose: () => void; onDone: (row: AppRow) => void }) {
  const definitions = useApi(() => listDefinitions(), []);
  const sports = [...new Set((definitions.data ?? []).map((row) => row.sportCode))].sort();

  const [draft, setDraftState] = useState<NewApp>(EMPTY_APP);
  const [codeTouched, setCodeTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const code = codeTouched ? draft.code : codeFromName(draft.name);
  // A change to any field answers the last problem, so the message goes with it.
  const setDraft = (next: NewApp) => {
    setDraftState(next);
    setProblem(null);
  };

  const save = async () => {
    const app: NewApp = {
      ...draft,
      name: draft.name.trim(),
      code: code.trim(),
      url: draft.url.trim(),
      sportCode: draft.sportCode?.trim() || null,
      description: draft.description?.trim() || null,
    };
    const found = newAppProblem(app);
    if (found) {
      setProblem(found);
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      onDone(await registerApp(app));
    } catch (thrown) {
      setProblem(errorText(thrown, "omnium did not register the app"));
      setSaving(false);
    }
  };

  return (
    <Dialog
      title="Register an app"
      note="Tell omnium where a web app lives, so it can open it in a frame. Next, choose which workflows open in it."
      onClose={onClose}
      footer={
        <>
          {problem && (
            <p role="alert" className={styles.alert}>
              {problem}
            </p>
          )}
          <Button tone="subtle" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button tone="primary" type="submit" form="register-app" disabled={saving}>
            {saving ? "Registering…" : "Register"}
          </Button>
        </>
      }
    >
      <form
        id="register-app"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Stack>
          <Field label="What does it do?" hint={KIND_HINT[draft.kind]}>
            <Segmented
              name="What the app does"
              value={draft.kind}
              options={[
                { value: "scoring", label: "Scores a fixture" },
                { value: "operations", label: "Runs an event" },
              ]}
              onChange={(kind) => setDraft({ ...draft, kind })}
            />
          </Field>
          <Field
            label="Name"
            required
            htmlFor="app-name"
            hint="What people call it, such as Ringside or Operations console."
          >
            <Input
              id="app-name"
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </Field>
          <Field
            label="Short code"
            required
            htmlFor="app-code"
            hint="Small letters, numbers and dashes. omnium uses it in links. It cannot be changed later."
          >
            <Input
              id="app-code"
              value={code}
              spellCheck={false}
              onChange={(event) => {
                setCodeTouched(true);
                setDraft({ ...draft, code: event.target.value });
              }}
            />
          </Field>
          <Field
            label="Address"
            required
            htmlFor="app-url"
            hint="Where the app is served, starting with https://. omnium only accepts messages from this address."
          >
            <Input
              id="app-url"
              value={draft.url}
              placeholder="https://desk.example.com/"
              spellCheck={false}
              onChange={(event) => setDraft({ ...draft, url: event.target.value })}
            />
          </Field>
          <Field
            label="Sport"
            htmlFor="app-sport"
            hint="Only for a desk built for one sport. It can then be used with that sport's workflows and the ones for every sport."
          >
            <Select
              id="app-sport"
              value={draft.sportCode ?? ""}
              onChange={(event) => setDraft({ ...draft, sportCode: event.target.value })}
            >
              <option value="">Any sport</option>
              {sports.map((sport) => (
                <option key={sport} value={sport}>
                  {displayName(sport)}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="What it is for"
            htmlFor="app-description"
            hint="One short sentence. People see it in the list of apps."
          >
            <Input
              id="app-description"
              value={draft.description ?? ""}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            />
          </Field>
        </Stack>
      </form>
    </Dialog>
  );
}

function RemoveAppDialog({
  app,
  onClose,
  onDone,
}: {
  app: AppRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const remove = async () => {
    setSaving(true);
    setProblem(null);
    try {
      await removeApp(app.id);
      onDone();
    } catch (thrown) {
      setProblem(errorText(thrown, "omnium did not remove the app"));
      setSaving(false);
    }
  };

  const names = app.workflows.map((workflow) => workflow.name);
  return (
    <Dialog
      title={`Remove ${app.name}?`}
      onClose={onClose}
      footer={
        <>
          {problem && (
            <p role="alert" className={styles.alert}>
              {problem}
            </p>
          )}
          <Button tone="subtle" disabled={saving} onClick={onClose}>
            Keep it
          </Button>
          <Button tone="danger" disabled={saving} onClick={() => void remove()}>
            {saving ? "Removing…" : "Remove app"}
          </Button>
        </>
      }
    >
      <p className={styles.text}>
        {names.length > 0
          ? `It will stop opening for ${names.join(", ")}.`
          : "No workflow opens in it yet."}{" "}
        Fixtures that picked it go back to their workflow&apos;s default app.
      </p>
      <p className={styles.text}>This cannot be undone. To use it again, register it again.</p>
    </Dialog>
  );
}
